import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Link, useParams, useNavigate } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Check, Plus, Trash2, Upload, FileUp, AlertTriangle, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ITPRegister() {
  const { user } = useAuth();
  const [itps, setItps] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const [v2List, setV2List] = useState([]);
  const nav = useNavigate();
  const reload = () => {
    api.get("/itp-templates").then(r => setItps(r.data));
    api.get("/v2/itp-templates").then(r => setV2List(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const createScratch = async () => {
    const name = window.prompt("Template name");
    if (!name || !name.trim()) return;
    try {
      const r = await api.post("/v2/itp-templates", { name: name.trim(), creation_mode: "scratch" });
      toast.success("Template created.");
      nav(`/itp/builder/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="itp-register-page">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <div className="overline mb-1">Register</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">ITP Templates</h1>
          <p className="mt-1 text-sm text-slate-600">Inspection & Test Plans available for use on lots.</p>
        </div>
        {can.createITP(user?.role) && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setOpen(true)} variant="ghost" className="text-slate-500" data-testid="create-itp-btn">
              <Plus className="mr-2 size-4"/> Legacy Create
            </Button>
            <Button onClick={() => setModeOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="create-itp-template-btn">
              <Plus className="mr-2 size-4"/> Create ITP Template
            </Button>
          </div>
        )}
      </div>
      <Dialog open={modeOpen} onOpenChange={setModeOpen}>
        <DialogContent className="max-w-lg" data-testid="create-mode-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create ITP Template</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            {[
              ["scratch", "Create from scratch", "Build the ITP with FlowQA's template builder — headings, fields, signatures and (soon) configurable inspection tables.", () => { setModeOpen(false); createScratch(); }],
              ["import", "Import existing ITP", "Upload your DOCX. FlowQA keeps the original, renders a preview and detects inspection rows for review.", () => { setModeOpen(false); setImportOpen(true); }],
              ["layout", "Use original document layout", "Keep the document exactly as-is and place interactive fields over the rendered pages.", () => { setModeOpen(false); setImportOpen(true); }],
            ].map(([key, title, desc, fn]) => (
              <button key={key} onClick={fn}
                className="rounded-md border border-slate-200 p-3 text-left hover:border-teal-600 hover:bg-teal-50"
                data-testid={`create-mode-${key}`}>
                <div className="text-sm font-bold text-slate-900">{title}</div>
                <div className="mt-0.5 text-xs text-slate-500">{desc}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {v2List.length > 0 && (
        <Card className="mb-4 overflow-hidden border-slate-200" data-testid="v2-templates-card">
          <div className="border-b border-slate-100 px-4 py-2.5">
            <span className="overline text-teal-700">Template Builder (v2)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Discipline</th>
                <th className="px-4 py-2.5">Rev</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Mode</th>
              </tr>
            </thead>
            <tbody>
              {v2List.map(t => (
                <tr key={t.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                  onClick={() => nav(`/itp/builder/${t.id}`)} data-testid={`v2-template-row-${t.id}`}>
                  <td className="px-4 py-2.5 font-semibold text-teal-700">{t.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{t.code || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{t.discipline || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    Rev {t.current_revision}
                    {t.draft_revision != null && t.draft_revision !== t.current_revision && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`draft-chip-${t.id}`}>
                        Rev {t.draft_revision} draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={t.status}/></td>
                  <td className="px-4 py-2.5 text-xs capitalize text-slate-500">{(t.creation_mode || "").replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">ITP Number</th>
              <th className="px-4 py-3">Template Name</th>
              <th className="px-4 py-3">Activity / Trade</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3 text-center">Rev</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {itps.map(t => <ITPRegisterRow key={t.id} t={t} canManage={can.createITP(user?.role)} reload={reload} />)}
            {itps.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No ITP templates yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      {open && <CreateITPDialog onClose={() => { setOpen(false); reload(); }} />}
      {importOpen && <ImportITPDialog onClose={() => { setImportOpen(false); reload(); }} />}
    </div>
  );
}

function ITPRegisterRow({ t, canManage, reload }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const isImported = t.output_format_type === "imported_docx_template";
  const rawStatus = t.status || "Draft";
  // Display-only mapping: backend stores legacy "Approved"/"Draft"; users see Active/Imported/etc.
  const displayStatus = (() => {
    if (rawStatus === "Approved") return "Published";
    if (rawStatus === "Draft" && isImported) return "Draft";
    return rawStatus;
  })();
  const isActive = rawStatus === "Approved";
  const isImportedDraft = rawStatus === "Draft" && isImported;
  const lastUpdated = t.updated_at || t.created_at;

  const act = async (path, label, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/itp-templates/${t.id}${path}`);
      toast.success(`${label} successful`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`itp-row-${t.itp_number}`}>
      <td className="px-4 py-3 font-mono font-semibold">{t.itp_number}</td>
      <td className="px-4 py-3 font-semibold text-slate-900">{t.name}</td>
      <td className="px-4 py-3 text-slate-600">{t.activity}</td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {t.project_name ? (
          <span className="truncate" title={t.project_name}>{t.project_number ? `${t.project_number} · ` : ""}{t.project_name.slice(0, 40)}</span>
        ) : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-center text-slate-600">{t.revision_number || "1"}</td>
      <td className="px-4 py-3 text-xs">
        {isImported ? (
          <span className="inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 font-semibold text-teal-700" data-testid={`itp-source-imported-${t.itp_number}`}>
            <FileUp className="size-3"/> Uploaded DOCX
          </span>
        ) : (
          <span className="text-slate-400">Manual</span>
        )}
      </td>
      <td className="px-4 py-3" data-testid={`itp-status-${t.itp_number}`}><StatusBadge status={displayStatus} /></td>
      <td className="px-4 py-3 text-xs text-slate-500">{lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "—"}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <Link to={`/itp/${t.id}`} className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100" data-testid={`open-itp-${t.itp_number}`}>View</Link>
          {isImported && !isActive && rawStatus !== "Superseded" && rawStatus !== "Archived" && canManage && (
            <Link to={`/itp/${t.id}/review`} className="rounded-md border border-teal-700 px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-50" data-testid={`review-itp-${t.itp_number}`}>Review Extraction</Link>
          )}
          {canManage && (isImportedDraft || rawStatus === "Draft") && (
            <Button size="sm" variant="outline" className="h-7 border-teal-700 text-xs text-teal-700 hover:bg-teal-50" onClick={() => act("/approve", "Activate")} disabled={busy} data-testid={`activate-itp-${t.itp_number}`}>
              {isImported ? "Confirm Mapping & Activate" : "Activate Template"}
            </Button>
          )}
          {isActive && (
            <Button
              size="sm"
              className="h-7 bg-teal-700 text-xs hover:bg-teal-800"
              onClick={() => nav(`/lots/new?itp=${t.id}`)}
              data-testid={`create-lot-from-itp-${t.itp_number}`}
            >Create Lot</Button>
          )}
          {canManage && isActive && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act("/supersede", "Supersede", "Mark this template as Superseded? It will no longer be used for new lots.")} disabled={busy} data-testid={`supersede-itp-${t.itp_number}`}>Supersede</Button>
          )}
          {canManage && rawStatus !== "Archived" && (
            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => act("/archive", "Archive", "Archive this template? It will be hidden from active workflows.")} disabled={busy} data-testid={`archive-itp-${t.itp_number}`}>Archive</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function CreateITPDialog({ onClose }) {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ itp_number: "", name: "", activity: "", specification_reference: "", revision_number: "1" });
  const [sections, setSections] = useState([{ section_name: "Section 1", items: [{ item_description: "", requirement: "", evidence_required: "", mandatory: true, linked_hold_point: "" }] }]);
  const [holdPoints, setHoldPoints] = useState([{ hp_number: "HP1", title: "", description: "", required_evidence: "" }]);
  const [saving, setSaving] = useState(false);

  const addSection = () => setSections([...sections, { section_name: `Section ${sections.length+1}`, items: [{ item_description: "", requirement: "", evidence_required: "", mandatory: true, linked_hold_point: "" }] }]);
  const addItem = (si) => {
    const copy = [...sections];
    copy[si].items.push({ item_description: "", requirement: "", evidence_required: "", mandatory: true, linked_hold_point: "" });
    setSections(copy);
  };
  const addHp = () => setHoldPoints([...holdPoints, { hp_number: `HP${holdPoints.length+1}`, title: "", description: "", required_evidence: "" }]);
  const removeHp = (i) => setHoldPoints(holdPoints.filter((_,idx)=>idx!==i));
  const removeItem = (si, ii) => {
    const copy = [...sections];
    copy[si].items = copy[si].items.filter((_,idx)=>idx!==ii);
    setSections(copy);
  };
  const removeSection = (si) => setSections(sections.filter((_,idx)=>idx!==si));

  const save = async () => {
    if (!meta.itp_number || !meta.name) { toast.error("ITP number and name required"); return; }
    const cleanSections = sections.filter(s => s.items.some(i => i.item_description.trim()))
      .map(s => ({ ...s, items: s.items.filter(i => i.item_description.trim()).map(i => ({ ...i, linked_hold_point: i.linked_hold_point || null })) }));
    const cleanHps = holdPoints.filter(hp => hp.title.trim()).map(hp => ({
      ...hp,
      required_evidence: hp.required_evidence ? hp.required_evidence.split(",").map(s => s.trim()).filter(Boolean) : [],
    }));
    if (cleanSections.length === 0) { toast.error("Add at least one ITP item"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/itp-templates", {
        ...meta, sections: cleanSections, hold_points: cleanHps, sign_off_roles: ["qa_manager", "client"],
      });
      toast.success("ITP saved as Draft");
      onClose();
      nav(`/itp/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="create-itp-dialog">
        <DialogHeader><DialogTitle>Create ITP Template</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          {[
            ["itp_number","ITP Number *","ITP-DR-002"],
            ["name","Name *","Stormwater Pit Installation"],
            ["activity","Activity",""],
            ["specification_reference","Specification Reference",""],
            ["revision_number","Revision Number",""],
          ].map(([k,l,p]) => (
            <div key={k} className={k==="itp_number" ? "" : ""}>
              <Label className="text-xs">{l}</Label>
              <Input value={meta[k]} onChange={e=>setMeta({...meta, [k]:e.target.value})} placeholder={p} data-testid={`itp-meta-${k}`}/>
            </div>
          ))}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Sections & ITP Items</h3>
            <Button size="sm" variant="outline" onClick={addSection} data-testid="add-section-btn"><Plus className="mr-1 size-3.5"/>Add Section</Button>
          </div>
          <Accordion type="multiple" defaultValue={["s0"]} className="space-y-2">
            {sections.map((s, si) => (
              <AccordionItem key={si} value={`s${si}`} className="rounded-md border border-slate-200 bg-white">
                <AccordionTrigger className="px-3 py-2">
                  <input value={s.section_name} onChange={e=>{const c=[...sections];c[si].section_name=e.target.value;setSections(c);}}
                    onClick={e=>e.stopPropagation()} className="border-none bg-transparent font-semibold focus:outline-none" data-testid={`section-name-${si}`}/>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  {s.items.map((it, ii) => (
                    <div key={ii} className="mb-2 grid grid-cols-1 gap-2 rounded border border-slate-100 p-2 md:grid-cols-12">
                      <Input className="md:col-span-5" placeholder="Item description" value={it.item_description} onChange={e=>{const c=[...sections];c[si].items[ii].item_description=e.target.value;setSections(c);}} data-testid={`item-desc-${si}-${ii}`}/>
                      <Input className="md:col-span-3" placeholder="Requirement" value={it.requirement} onChange={e=>{const c=[...sections];c[si].items[ii].requirement=e.target.value;setSections(c);}}/>
                      <Input className="md:col-span-2" placeholder="Evidence req'd" value={it.evidence_required} onChange={e=>{const c=[...sections];c[si].items[ii].evidence_required=e.target.value;setSections(c);}}/>
                      <Input className="md:col-span-1" placeholder="HP#" value={it.linked_hold_point} onChange={e=>{const c=[...sections];c[si].items[ii].linked_hold_point=e.target.value;setSections(c);}}/>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(si, ii)} className="md:col-span-1 text-red-600 hover:bg-red-50"><Trash2 className="size-4"/></Button>
                    </div>
                  ))}
                  <div className="flex justify-between">
                    <Button size="sm" variant="outline" onClick={() => addItem(si)} data-testid={`add-item-${si}`}><Plus className="mr-1 size-3.5"/>Add Item</Button>
                    {sections.length > 1 && <Button size="sm" variant="ghost" onClick={() => removeSection(si)} className="text-red-600 hover:bg-red-50">Remove section</Button>}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Hold Points</h3>
            <Button size="sm" variant="outline" onClick={addHp} data-testid="add-hp-btn"><Plus className="mr-1 size-3.5"/>Add Hold Point</Button>
          </div>
          <div className="space-y-2">
            {holdPoints.map((hp, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 rounded border border-slate-200 p-2 md:grid-cols-12">
                <Input className="md:col-span-2" placeholder="HP1" value={hp.hp_number} onChange={e=>{const c=[...holdPoints];c[i].hp_number=e.target.value;setHoldPoints(c);}} data-testid={`hp-number-${i}`}/>
                <Input className="md:col-span-4" placeholder="Title" value={hp.title} onChange={e=>{const c=[...holdPoints];c[i].title=e.target.value;setHoldPoints(c);}} data-testid={`hp-title-${i}`}/>
                <Input className="md:col-span-3" placeholder="Description" value={hp.description} onChange={e=>{const c=[...holdPoints];c[i].description=e.target.value;setHoldPoints(c);}}/>
                <Input className="md:col-span-2" placeholder="Evidence (comma-sep)" value={hp.required_evidence} onChange={e=>{const c=[...holdPoints];c[i].required_evidence=e.target.value;setHoldPoints(c);}}/>
                <Button size="sm" variant="ghost" onClick={() => removeHp(i)} className="md:col-span-1 text-red-600 hover:bg-red-50"><Trash2 className="size-4"/></Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="itp-save-draft">
            {saving ? "Saving…" : "Save as Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ITPDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);

  const load = () => api.get(`/itp-templates/${id}`).then(r => setT(r.data));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div className="p-8 text-slate-500">Loading…</div>;

  const isImportedTpl = t.output_format_type === "imported_docx_template";
  const rawStatus = t.status || "Draft";
  const displayStatus = (() => {
    if (rawStatus === "Approved") return "Published";
    if (rawStatus === "Draft" && isImportedTpl) return "Imported";
    return rawStatus;
  })();
  const isActive = rawStatus === "Approved";

  const activate = async () => {
    try { await api.post(`/itp-templates/${id}/approve`); toast.success("Template activated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="itp-detail-page">
      <Link to="/itp" className="text-sm text-slate-500 hover:underline">← ITP Templates</Link>
      <div className="mt-3 flex items-start justify-between gap-4">
        <div>
          <div className="overline text-teal-700">{t.itp_number} · Rev {t.revision_number}</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">{t.name}</h1>
          <p className="mt-1 text-slate-600">{t.activity} · {t.specification_reference}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={displayStatus} />
          {can.approveITP(user?.role) && !isActive && (
            <Button onClick={activate} className="bg-teal-700 hover:bg-teal-800" data-testid="approve-itp-btn">
              <Check className="mr-1.5 size-4" />{isImportedTpl ? "Confirm Mapping & Publish" : "Publish ITP Template"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-2 font-heading text-lg font-bold tracking-tight">ITP Items</h2>
          <Accordion type="multiple" defaultValue={t.sections.map(s => s.section_name)} className="space-y-2">
            {t.sections.map((s) => (
              <AccordionItem key={s.section_name} value={s.section_name} className="rounded-md border border-slate-200 bg-white">
                <AccordionTrigger className="px-4 py-3 text-left font-heading text-base font-semibold">
                  {s.section_name} <span className="ml-2 text-xs font-normal text-slate-500">{s.items.length} items</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr><th className="py-2">Item</th><th className="py-2">Requirement</th><th className="py-2">Evidence</th><th className="py-2">HP</th></tr>
                    </thead>
                    <tbody>
                      {s.items.map((it, idx) => (
                        <tr key={`${s.section_name}-${it.item_description}-${idx}`} className="border-t border-slate-100">
                          <td className="py-2 pr-2">{it.item_description} {it.mandatory ? <span className="text-red-600">*</span> : null}</td>
                          <td className="py-2 pr-2 text-slate-600">{it.requirement}</td>
                          <td className="py-2 pr-2 text-slate-600">{it.evidence_required}</td>
                          <td className="py-2 pr-2 font-mono text-xs text-teal-700">{it.linked_hold_point || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div>
          <h2 className="mb-2 font-heading text-lg font-bold tracking-tight">Hold Points / Action Points</h2>
          <div className="space-y-2">
            {t.hold_points.map(hp => (
              <Card key={hp.hp_number} className="border-slate-200 p-3" data-testid={`hp-tpl-${hp.hp_number}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs font-bold text-teal-700">{hp.hp_number}</div>
                  <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-teal-800">HP</span>
                </div>
                <div className="font-semibold text-slate-900">{hp.title}</div>
                <div className="mt-1 text-xs text-slate-600">{hp.description}</div>
                {hp.required_evidence?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {hp.required_evidence.map((e,i) => <span key={`${hp.hp_number}-evidence-${e}-${i}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{e}</span>)}
                  </div>
                )}
              </Card>
            ))}
            {(t.approval_points || []).map(ap => (
              <Card key={ap.hp_number} className="border-indigo-200 p-3" data-testid={`ap-tpl-${ap.hp_number}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs font-bold text-indigo-700">{ap.hp_number}</div>
                  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-800">AP</span>
                </div>
                <div className="font-semibold text-slate-900">{ap.title}</div>
                <div className="mt-1 text-xs text-slate-600">{ap.description}</div>
                {ap.required_evidence?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {ap.required_evidence.map((e,i) => <span key={`${ap.hp_number}-evidence-${e}-${i}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{e}</span>)}
                  </div>
                )}
              </Card>
            ))}
            {t.hold_points.length === 0 && (!t.approval_points || t.approval_points.length === 0) && (
              <div className="text-xs text-slate-500">No hold points or action points defined.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function ImportITPDialog({ onClose }) {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("none");
  const [activity, setActivity] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [revision, setRevision] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)).catch(() => {}); }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".docx")) {
      toast.error("Only .docx files are supported");
      return;
    }
    setFile(f);
    if (!templateName) setTemplateName(f.name.replace(/\.docx$/i, ""));
  };

  const submit = async () => {
    if (!file) { toast.error("Please choose a .docx file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (projectId && projectId !== "none") fd.append("project_id", projectId);
      // Optional metadata fields the user provided (sent as form fields for traceability)
      if (activity) fd.append("activity_hint", activity);
      if (templateName) fd.append("template_name_hint", templateName);
      if (revision) fd.append("revision_hint", revision);
      if (notes) fd.append("notes", notes);
      const { data } = await api.post("/itp-templates/import-docx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(data.message || "Your ITP was uploaded successfully.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const openImported = () => {
    if (result?.itp_template_id) nav(`/itp/${result.itp_template_id}/review`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="import-itp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="size-5 text-teal-700"/> Upload ITP (.docx)
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            <div className="rounded-md border border-dashed border-teal-300 bg-teal-50/50 p-4">
              <Label className="text-xs">DOCX File *</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".docx"
                onChange={onPick}
                className="hidden"
                data-testid="import-itp-file-input"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-itp-choose-file">
                  <Upload className="mr-2 size-4"/> Choose .docx
                </Button>
                <span className="text-xs text-slate-600" data-testid="import-itp-filename">
                  {file ? file.name : "No file selected"}
                </span>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                FlowQA saves your original document unchanged, generates a PDF preview, and attempts to detect inspection items automatically. If detection fails, you can map rows manually.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs">Project (optional)</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger data-testid="import-itp-project"><SelectValue placeholder="No project link"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project link</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.number} — {p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">ITP Category / Activity</Label>
                <Input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="e.g. Concrete Paving" data-testid="import-itp-activity"/>
              </div>
              <div>
                <Label className="text-xs">Template Name</Label>
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Stormwater Pit Installation" data-testid="import-itp-name"/>
              </div>
              <div>
                <Label className="text-xs">Revision</Label>
                <Input value={revision} onChange={(e) => setRevision(e.target.value)} placeholder="e.g. 1" data-testid="import-itp-revision"/>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Add any context for QA review…" data-testid="import-itp-notes"/>
              </div>
            </div>
          </div>
        )}

        {result && <UploadProgressPanel result={result} />}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="import-itp-cancel">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result ? (
            <Button onClick={submit} disabled={uploading || !file} className="bg-teal-700 hover:bg-teal-800" data-testid="import-itp-submit">
              {uploading ? <><Loader2 className="mr-2 size-4 animate-spin"/>Uploading…</> : <><FileUp className="mr-2 size-4"/>Upload ITP</>}
            </Button>
          ) : (
            <Button onClick={openImported} className="bg-teal-700 hover:bg-teal-800" data-testid="import-itp-open">
              Review & Map ITP
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadProgressPanel({ result }) {
  const [proc, setProc] = useState(null);
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/itp-templates/${result.itp_template_id}/processing-status`);
        if (stop) return;
        setProc(r.data);
        if (r.data.render_status === "processing") setTimeout(poll, 2000);
      } catch {
        if (!stop) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [result.itp_template_id]);

  const render = proc?.render_status || "processing";
  const detection = result.detection_status || proc?.automatic_detection_status || "Completed";
  const detectionFailed = detection.startsWith("Failed");
  const stepState = (st) => st === "done"
    ? <Check className="size-3.5 text-emerald-600"/>
    : st === "busy" ? <Loader2 className="size-3.5 animate-spin text-teal-700"/>
    : st === "failed" ? <AlertTriangle className="size-3.5 text-rose-600"/>
    : st === "warn" ? <AlertTriangle className="size-3.5 text-amber-600"/>
    : <span className="inline-block size-3.5 rounded-full border border-slate-300"/>;
  const steps = [
    ["Uploading DOCX", "done"],
    ["Saving original file", "done"],
    ["Converting document", render === "completed" ? "done" : render === "failed" ? "failed" : "busy"],
    ["Detecting ITP items", detectionFailed ? "warn" : "done"],
    ["Ready for review", render === "processing" ? "pending" : "done"],
  ];
  return (
    <div className="space-y-3" data-testid="import-itp-result">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        <div className="font-heading text-base font-bold">Your ITP was uploaded successfully.</div>
        <div className="mt-1 text-xs"><b>{result.itp_number}</b> — {result.itp_name}</div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
          <div><div className="overline">Sections</div><div className="font-semibold">{result.sections_count}</div></div>
          <div><div className="overline">Hold Points</div><div className="font-semibold">{result.hold_points_count}</div></div>
          <div><div className="overline">Detection</div><div className="font-semibold">{detection}</div></div>
        </div>
      </div>
      <div className="rounded-md border border-slate-200 p-3" data-testid="upload-progress-steps">
        {steps.map(([label, st]) => (
          <div key={label} className="flex items-center gap-2 py-1 text-xs text-slate-700">
            {stepState(st)}
            <span className={st === "pending" ? "text-slate-400" : ""}>{label}</span>
          </div>
        ))}
      </div>
      {detectionFailed && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="detection-failed-message">
          Your ITP was uploaded successfully. FlowQA could not automatically detect some inspection rows because the
          document contains complex table formatting. Review and map the affected rows before publishing the ITP Template.
        </div>
      )}
      {render === "failed" && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="render-failed-message">
          {proc?.render_error || "FlowQA saved the original ITP, but could not generate the document preview. The file has not been lost. Retry conversion or download the original document."}
        </div>
      )}
      {!detectionFailed && (result.warnings || []).length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="import-itp-warnings">
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="size-3.5"/> Detection warnings ({result.warnings.length})
          </div>
          <ul className="ml-4 list-disc space-y-0.5">
            {result.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
            {result.warnings.length > 8 && <li>…and {result.warnings.length - 8} more (shown on the review screen)</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
